/*
 * AuthContext.jsx — Supabase Auth provider for HAVN.
 *
 * Supabase profiles table (run in Supabase SQL Editor):
 *
 * CREATE TABLE profiles (
 *   id UUID PRIMARY KEY REFERENCES auth.users(id) ON DELETE CASCADE,
 *   full_name TEXT,
 *   email TEXT,
 *   created_at TIMESTAMPTZ DEFAULT now(),
 *   onboarding_complete BOOLEAN DEFAULT FALSE,
 *   settings JSONB DEFAULT '{}'::jsonb
 * );
 *
 * ALTER TABLE profiles ENABLE ROW LEVEL SECURITY;
 * CREATE POLICY "Users can read own profile" ON profiles FOR SELECT USING (auth.uid() = id);
 * CREATE POLICY "Users can update own profile" ON profiles FOR UPDATE USING (auth.uid() = id);
 *
 * -- Auto-create profile on signup
 * CREATE OR REPLACE FUNCTION public.handle_new_user()
 * RETURNS trigger AS $$
 * BEGIN
 *   INSERT INTO public.profiles (id, full_name, email)
 *   VALUES (new.id, new.raw_user_meta_data->>'full_name', new.email);
 *   RETURN new;
 * END;
 * $$ LANGUAGE plpgsql SECURITY DEFINER;
 *
 * CREATE TRIGGER on_auth_user_created
 *   AFTER INSERT ON auth.users
 *   FOR EACH ROW EXECUTE FUNCTION public.handle_new_user();
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { syncUserToExtension } from '@/components/ThemePicker';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from profiles table
  const fetchProfile = async (userId) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      setProfile(data);
      return data;
    } catch {
      setProfile(null);
      return null;
    }
  };

  // Restore and VALIDATE session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // getSession() only reads localStorage — it does NOT validate the token
        const { data: { session: cached } } = await supabase.auth.getSession();

        if (cached) {
          // Validate the token against Supabase server
          const { data: { user: validUser }, error } = await supabase.auth.getUser();
          if (error || !validUser) {
            // Token expired or invalid — sign out to clear stale localStorage session
            console.warn('[AUTH] Cached session invalid, signing out:', error?.message);
            await supabase.auth.signOut();
            setUser(null);
            setSession(null);
            setProfile(null);
            setLoading(false);
            return;
          }
          // Token is valid
          setSession(cached);
          setUser(validUser);
          fetchProfile(validUser.id);
        } else {
          setUser(null);
          setSession(null);
        }
      } catch (err) {
        console.error('[AUTH] Init error:', err);
        // On any error, clear auth state so user sees login
        setUser(null);
        setSession(null);
      }
      setLoading(false);
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id);
        // Push the authoritative account id to the browser extension centrally,
        // on every auth resolution — not only when HomePage happens to be
        // mounted. This keeps extension-saved favorites keyed to the SAME id the
        // web app reads them under, so saves made from the extension appear in
        // the web app's saved list (previously they could land under a stale
        // anonymous/local id and never show up).
        try { syncUserToExtension(s.user.id); } catch (_) {}
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Migrate old localStorage identity on first login
  const migrateLocalStorage = async (userId) => {
    try {
      const oldFirstRun = localStorage.getItem('havn_first_run');
      if (oldFirstRun === 'complete') {
        await supabase.from('profiles').update({ onboarding_complete: true }).eq('id', userId);
      }
      // Clear old identity keys
      localStorage.removeItem('havn_user_id');
    } catch {
      // Non-fatal
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.full_name || metadata.fullName || '',
          first_name: metadata.first_name || '',
          last_name: metadata.last_name || '',
          phone: metadata.phone || '',
          age_range: metadata.age_range || '',
          referral_source: metadata.referral_source || '',
        },
      },
    });
    if (error) throw error;
    return data;
  };

  /**
   * Google OAuth — the one-tap door. No password, no confirmation email.
   * `redirectTo` lets a caller come back to the exact page it started on
   * (the caregiver invite page passes its full URL so the ?token= survives).
   *
   * Requires: Supabase → Authentication → Providers → Google enabled, and
   * the redirect URLs allow-listed under Authentication → URL Configuration.
   */
  const signInWithGoogle = async (redirectTo) => {
    const prodUrl = 'https://havnassist.up.railway.app';
    const origin = window.location.hostname === 'localhost' ? prodUrl : window.location.origin;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || `${origin}/home`,
        // Always let the user pick which Google account — a caregiver and a
        // senior often share a device.
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Migrate old localStorage data on login
    if (data.user) migrateLocalStorage(data.user.id);
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const resetPassword = async (email) => {
    // Use production URL if available, otherwise current origin
    const prodUrl = 'https://havnassist.up.railway.app';
    const origin = window.location.hostname === 'localhost' ? prodUrl : window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    fetchProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
